import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useNotification } from "../notification.hook";
import { RefreshReactQuery } from "../../constants/AppConstants";
import {
  DATA_SAVED_SUCCESS_LABEL,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
} from "../../constants/Dictionary";
import CmSheetGoalsService from "../../services/api/cmSheet/CmSheetGoals.service";
import { UpdateCollaboratorCmSheetGoalsRequest } from "../../types/api/collaborators.type";
import { COLLABORATOR_ADVANCEMENT_QUERY_KEY } from "../collaboratorProfile.hook";
import { CM_SHEET_STATE_MACHINE_QUERY_KEY } from "./stateMachine.hook";

export const CM_SHEET_GOALS_QUERY_KEY = "get-cm-sheet-goals";

export const useCmSheetGoals = (cmSheetId: number, enabled = true) => {
  const { notification } = useNotification();
  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: [CM_SHEET_GOALS_QUERY_KEY, cmSheetId],
    queryFn: () => CmSheetGoalsService.get(cmSheetId),
    staleTime: RefreshReactQuery.MODERATE,
    enabled,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

export const useUpdateCmSheetGoals = (
  cmSheetId: number,
  collaboratorId: number,
) => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync: updateGoals, isPending: isUpdating } = useMutation({
    mutationFn: (body: UpdateCollaboratorCmSheetGoalsRequest) =>
      CmSheetGoalsService.update(cmSheetId, body),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [CM_SHEET_GOALS_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [CM_SHEET_STATE_MACHINE_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [COLLABORATOR_ADVANCEMENT_QUERY_KEY, collaboratorId],
        }),
      ]);
      notification.success(DATA_SAVED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(error);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { updateGoals, isUpdating };
};
